import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiRouteConstants } from '../../../../../../core/constants/api-routes';
import { ApplicationCreationResponse } from '../../../../shared/models/responses/application-creation-response';
import { GoApplicationRequest } from '../../models/go-application-request';
import { GoFrequencyResultsResponse } from '../../../go-frequency-results/models/go-frequency-results-response';
import { CalculateGoApplicationPriceResponse } from '../../../go-frequency-results/models/calculate-go-application-price-response';
import { CalculateGoApplicationPriceRequest } from '../../../go-frequency-results/models/calculate-go-application-price-request';
import { GoSubmitToEwaRequest } from '../../../go-frequency-results/models/go-submit-to-ewa-request';

@Injectable({
    providedIn: 'root',
})
export class GoApplicationService {
    private readonly goApplicationApiRoute = ApiRouteConstants.GoApplication;

    constructor(private readonly http: HttpClient) {}

    createApplication(
        request: GoApplicationRequest,
    ): Observable<ApplicationCreationResponse> {
        return this.http.post<ApplicationCreationResponse>(
            this.goApplicationApiRoute.rootUrl,
            request,
        );
    }

    /**
     * Updates an existing Go application with the provided request payload.
     * @param applicationId The ID of the application to update.
     * @param request The updated Go application data
     */
    updateApplication(
        applicationId: string,
        request: GoApplicationRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getRootUrlWithApplicationId(
                applicationId,
            ),
            request,
        );
    }

    /**
     * Retrieves the Go application's latest request parameters by its application ID.
     * @param applicationId The ID of the application to retrieve.
     * @returns The Go application request parameters.
     */
    getParameters(applicationId: string): Observable<GoApplicationRequest> {
        return this.http.get<GoApplicationRequest>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'parameters',
                applicationId,
            ),
        );
    }

    /**
     * Terminates a go application search by its ID.
     * @param id The ID of the application to terminate.
     */
    terminateSearch(applicationId: string): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'terminateSearch',
                applicationId,
            ),
            {},
        );
    }

    /**
     * Cancels a go application search by its ID.
     * @param applicationId The ID of the application to cancel.
     */
    cancelSearch(applicationId: string): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'cancelSearch',
                applicationId,
            ),
            {},
        );
    }

    stopAndSaveSearch(applicationId: string): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'stopAndSaveSearch',
                applicationId,
            ),
            {},
        );
    }

    getFrequencyResults(
        applicationId: string,
    ): Observable<GoFrequencyResultsResponse> {
        return this.http.get<GoFrequencyResultsResponse>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'frequencyResults',
                applicationId,
            ),
        );
    }

    calculatePrice(
        applicationId: string,
        request: CalculateGoApplicationPriceRequest,
    ): Observable<CalculateGoApplicationPriceResponse> {
        return this.http.post<CalculateGoApplicationPriceResponse>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'price',
                applicationId,
            ),
            request,
        );
    }

    submitToEwa(
        applicationId: string,
        request: GoSubmitToEwaRequest,
    ): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'submitToEwa',
                applicationId,
            ),
            request,
        );
    }

    reRunExpiredApplication(applicationId: string): Observable<void> {
        return this.http.post<void>(
            this.goApplicationApiRoute.getFullRouteWithApplicationId(
                'reRunExpiredApplication',
                applicationId,
            ),
            {},
        );
    }
}
